<template>
  <Slide class="partners-slide">
    <div class="partners-header">
      <div class="partners-title">
        <SlideTitle type="with-menu" :items="slideMenuItems">{{ getCurrentStatisticTypeLabel }}</SlideTitle>
        <p class="partners-description">{{ getCurrentStatisticTypeDescription }}</p>
      </div>
      <div class="partners-filters">
        <SlideFilters
          :products="productFilterOptions"
          :selected-product="{ id: getSelectedProductFilter || 'all' }"
          :partners-disable="true"
          :partners="[]"
          :selected-partners="{}"
          :period-items="periodFilterOptions"
          :period-selected-item="{ id: getSelectedPeriodMode || 'current_month' }"
          :period="{ range: true, mode: getSelectedPeriodMode, ...periodFilterLimits, ...calendarConfig, selectedPeriod: { startDate: getActiveFilters.period[0] || null, endDate: getActiveFilters.period[1] || null }}"
          :options="{ eventMap: periodCalendarEventMap }"
        />
      </div>
    </div>
    <section class="partners-content">
      <div class="partners-chart">
        <PartnersChartSkeleton :is-loading="!isPartnersDataReady" class="chart-skeleton">
          <PartnersChart
            title="За календарний рік"
            :partners="getCurrentChartOptions"
            :type="selectedStatisticType"
            @update-slider="$emit('update-slider', $event)"
          >
            <EmptyChart v-if="isChartEmpty">
              <p class="message-title">{{ getEmptyOrdersChartMessage }}</p>
            </EmptyChart>
          </PartnersChart>
        </PartnersChartSkeleton>
      </div>
    </section>
  </Slide>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PartnersChart from './partners-chart';
import PartnersChartSkeleton from './partners-chart-skeleton';
import {
  PARTNERS_PERIOD_FILTER_EVENTS,
  PARTNERS_PRODUCT_FILTER_EVENTS,
  PARTNERS_STATS_EVENTS,
  partnersPeriodCalendarEventMap,
  partnersPeriodFilterOptions,
  partnersProductFilterOptions,
  partnersSlideMenuItems,
  partnersStatisticTypes,
  periodFilterLimits
} from '@/features/order-statistics/config';
import { filterCalculator } from '@/features/order-statistics/lib';
import { calendarConfig } from '@/shared/lib/calendar';
import { EmptyChart } from '@/shared/ui/chart';
import { POPUP_EVENTS } from '@/shared/ui/popup';
import { Slide, SlideFilters, SlideTitle } from '@/shared/ui/slider';

export default {
  data() {
    return {
      slideMenuItems: partnersSlideMenuItems,
      productFilterOptions: partnersProductFilterOptions,
      periodFilterOptions: partnersPeriodFilterOptions,
      periodFilterLimits,
      periodCalendarEventMap: partnersPeriodCalendarEventMap,
      selectedStatisticType: partnersStatisticTypes[0].value,
    };
  },
  created() {
    this._eventHandlers = {
      onStatsAmount: () => this.setSelectedStatisticType('amount'),
      onStatsCount: () => this.setSelectedStatisticType('count'),
      onStatsEarnings: () => this.setSelectedStatisticType('earnings'),
      onProductAll: () => this.setSelectedFilter('product', null, false),
      onProductOsago: () => this.setSelectedFilter('product', 'osago', true),
      onProductGreenCard: () => this.setSelectedFilter('product', 'green_card', true),
      onProductTourism: () => this.setSelectedFilter('product', 'tourism', true),
      onProductHome: () => this.setSelectedFilter('product', 'home', true),
      onProductExternal: () => this.setSelectedFilter('product', 'manual', true),
      onProductMiniKasko: () => this.setSelectedFilter('product', 'miniKasko', true),
      onProductPets: () => this.setSelectedFilter('product', 'pets', true),
      onPeriodCurrentMonth: () => { this.setPeriodMode(null); this.setSelectedFilter('period', [null, null], false); },
      onPeriodDay: () => this.setPeriodMode('days'),
      onPeriodWeek: () => this.setPeriodMode('weeks'),
      onPeriodMonth: () => this.setPeriodMode('months'),
      onPeriodDatesRange: (event) => this.setSelectedFilter('period', [event.startDate, event.endDate]),
    };

    this.fetchPartnersStatistics();

    this.$eventBus.$on(PARTNERS_STATS_EVENTS.PARTNERS_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$on(PARTNERS_STATS_EVENTS.PARTNERS_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$on(PARTNERS_STATS_EVENTS.PARTNERS_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$on(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$on(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_CURRENT_MONTH, this._eventHandlers.onPeriodCurrentMonth);
    this.$eventBus.$on(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$on(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$on(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$on(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  beforeDestroy() {
    this.$eventBus.$off(PARTNERS_STATS_EVENTS.PARTNERS_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$off(PARTNERS_STATS_EVENTS.PARTNERS_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$off(PARTNERS_STATS_EVENTS.PARTNERS_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$off(PARTNERS_PRODUCT_FILTER_EVENTS.PARTNERS_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$off(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_CURRENT_MONTH, this._eventHandlers.onPeriodCurrentMonth);
    this.$eventBus.$off(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$off(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$off(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$off(PARTNERS_PERIOD_FILTER_EVENTS.PARTNERS_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  mixins: [ calendarConfig, filterCalculator ],
  components: { PartnersChartSkeleton, PartnersChart, EmptyChart, SlideFilters, SlideTitle, Slide },
  computed: {
    ...mapGetters('statisticPartnersModule', [
      'isChartEmpty', 'getEmptyOrdersChartMessage', 'getStatisticChartOptions', 'isPartnersDataReady', 'getActiveFilters',
    ]),
    getCurrentStatisticType() { return partnersStatisticTypes.find(type => type.value === this.selectedStatisticType); },
    getCurrentStatisticTypeLabel() { return this.getCurrentStatisticType?.label || ''; },
    getCurrentStatisticTypeDescription() { return this.getCurrentStatisticType?.description || ''; },
    getCurrentChartOptions() { return this.getStatisticChartOptions(this.selectedStatisticType); },
    getSelectedProductFilter() { return this.getActiveFilters?.product || null; },
    getSelectedPeriodMode() { return this.getActiveFilters?.periodMode || null; },
  },
  methods: {
    ...mapActions('statisticPartnersModule', ['fetchPartnersStatistics', 'setupFilter', 'setupPeriodMode']),
    setSelectedStatisticType(type) {
      if (this.selectedStatisticType !== type) {
        this.selectedStatisticType = type;
      }

      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    setSelectedFilter(type, value, multiselect = false, refetchData = true) {
      this.setupFilter({
        filters: this.calculateFilterValues(this.getActiveFilters, type, value, multiselect),
        mode: 'update',
        refetchData
      });
      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    setPeriodMode(mode) {
      if (this.getSelectedPeriodMode !== mode) {
        this.setupPeriodMode(mode);
      }
    },
  },
  watch: {
    isPartnersDataReady: {
      immediate: false,
      handler(isReady) {
        if (isReady) {
          this.$emit('update-slider');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.partners-slide {
  min-height: 785px;
  padding: 24px 24px 65px;

  @media (max-width: 450px) {
    padding: 24px 24px 120px;
  }
}

.partners-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;

  @media (max-width: 1050px) {
    flex-direction: column;
    align-items: start;
  }
}

.partners-description {
  font-size: 14px;
  font-weight: 400;
  color: #71717A;
}

.message-title {
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 20px;
  text-align: center;
}

.chart-skeleton {
  min-height: 630px;
}

@media (max-width: 1050px) {
  .partners-title {
    margin-bottom: 16px;
  }
}
</style>
